import React from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  DocumentIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

/** `size` is in bytes, 0 for a file the editor cannot see. */
export type DesignPageState = { fileId: string; name: string; printable: boolean; size: number };

export type IncludedPagesState = { before: DesignPageState[]; after: DesignPageState[] };

type Side = keyof IncludedPagesState;

const SIDES: { key: Side; label: string }[] = [
  { key: 'before', label: 'Start pages' },
  { key: 'after', label: 'End pages' }
];

const isPdf = (file: File) =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

const MB = 1024 * 1024;

/** Mirrors brand.MaxIncludedPagesBytes. */
const MAX_PAGE_BYTES = 8 * MB;
/** Mirrors maxDesignPagesPerSide in the api package. */
const MAX_PAGES_PER_SIDE = 10;

export const PAGES_TOO_LARGE = `Start and end pages can total at most ${MAX_PAGE_BYTES / MB}\u00a0MB. Remove a page or choose smaller PDFs.`;

const totalBytes = ({ before, after }: IncludedPagesState) =>
  [...before, ...after].reduce((sum, page) => sum + page.size, 0);

/**
 * As the server holds it, the limit binds only a save that adds a page: a design converted from a
 * Style may already be over it, and still saves the pages it keeps.
 */
export const pagesTooLarge = (pages: IncludedPagesState, saved: IncludedPagesState): boolean => {
  const kept = new Set([...saved.before, ...saved.after].map((page) => page.fileId));
  return (
    totalBytes(pages) > MAX_PAGE_BYTES &&
    [...pages.before, ...pages.after].some((page) => !kept.has(page.fileId))
  );
};

export const samePages = (a: IncludedPagesState, b: IncludedPagesState): boolean =>
  SIDES.every(
    ({ key }) =>
      a[key].length === b[key].length && a[key].every((page, i) => page.fileId === b[key][i].fileId)
  );

/** Sized to the page row's text, so reordering does not make the rows taller. */
const MoveButton = ({
  label,
  disabled,
  onClick,
  children
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={disabled}
    onClick={onClick}
    className="shrink-0 rounded p-0.5 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 disabled:opacity-30 disabled:hover:bg-transparent dark:text-neutral-400 dark:hover:bg-white/10 dark:hover:text-neutral-100"
  >
    {children}
  </button>
);

/** The PDFs a design prints around a document. Files are held locally until the design is saved. */
export const IncludedPagesFields = ({
  pages,
  saved,
  onChange,
  stage
}: {
  pages: IncludedPagesState;
  /** The pages as the server holds them, which a save may keep whatever they weigh. */
  saved: IncludedPagesState;
  onChange: (pages: IncludedPagesState) => void;
  /** Holds a picked file until save and returns the id it stands under meanwhile. */
  stage: (file: File) => string;
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const add = (side: Side, files: File[]) => {
    if (files.some((file) => !isPdf(file) || file.size === 0 || file.size > MAX_PAGE_BYTES)) {
      setError(`Choose PDFs up to ${MAX_PAGE_BYTES / MB}\u00a0MB.`);
      return;
    }
    if (pages[side].length + files.length > MAX_PAGES_PER_SIDE) {
      setError(`Up to ${MAX_PAGES_PER_SIDE} PDFs each for start and end pages.`);
      return;
    }
    setError(null);
    const added = files.map((file) => ({
      fileId: stage(file),
      name: file.name,
      printable: true,
      size: file.size
    }));
    onChange({ ...pages, [side]: [...pages[side], ...added] });
  };
  const total = totalBytes(pages);
  const move = (side: Side, from: number, to: number) => {
    const reordered = [...pages[side]];
    reordered.splice(to, 0, ...reordered.splice(from, 1));
    onChange({ ...pages, [side]: reordered });
  };

  return (
    <section>
      <h3 className="text-sm font-medium text-neutral-900 dark:text-white">Start and end pages</h3>
      <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
        PDFs printed before and after documents.
      </p>
      {pagesTooLarge(pages, saved) ? (
        <p
          role="alert"
          className="mt-2 flex items-center gap-1 text-xs text-red-600 dark:text-red-400"
        >
          <ExclamationCircleIcon className="size-3.5 shrink-0" />
          {PAGES_TOO_LARGE}
        </p>
      ) : (
        total > MAX_PAGE_BYTES && (
          <p className="mt-2 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
            <ExclamationTriangleIcon className="size-3.5 shrink-0" />
            These pages total {(total / MB).toFixed(1)}&nbsp;MB. Previews may fail until smaller
            PDFs are uploaded.
          </p>
        )
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      {SIDES.map((side) => (
        <div key={side.key} className="mt-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
              {side.label}
            </p>
            {pages[side.key].length < MAX_PAGES_PER_SIDE && (
              <label className="relative cursor-pointer rounded-sm text-xs text-sky-600 hover:underline dark:text-sky-400 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-primary-500">
                Add PDF
                <input
                  type="file"
                  accept=".pdf,application/pdf"
                  multiple
                  className="sr-only"
                  aria-label={`Add a PDF to ${side.label.toLowerCase()}`}
                  onChange={(e) => {
                    add(side.key, Array.from(e.target.files ?? []));
                    e.target.value = '';
                  }}
                />
              </label>
            )}
          </div>
          <ul className="mt-1.5 space-y-1.5">
            {pages[side.key].map((page, index, all) => (
              <li
                key={page.fileId}
                className="rounded-md border border-neutral-200 px-2.5 py-1.5 dark:border-neutral-700"
              >
                <div className="flex items-center gap-2">
                  <DocumentIcon className="size-4 shrink-0 text-neutral-400 dark:text-neutral-500" />
                  <span
                    className="min-w-0 flex-1 truncate text-xs text-neutral-700 dark:text-neutral-300"
                    title={page.name}
                  >
                    {page.name}
                  </span>
                  {all.length > 1 && (
                    <>
                      <MoveButton
                        label={`Move ${page.name} up`}
                        disabled={index === 0}
                        onClick={() => move(side.key, index, index - 1)}
                      >
                        <ArrowUpIcon className="size-3.5" />
                      </MoveButton>
                      <MoveButton
                        label={`Move ${page.name} down`}
                        disabled={index === all.length - 1}
                        onClick={() => move(side.key, index, index + 1)}
                      >
                        <ArrowDownIcon className="size-3.5" />
                      </MoveButton>
                    </>
                  )}
                  <button
                    type="button"
                    aria-label={`Remove ${page.name}`}
                    className="text-xs text-neutral-500 hover:text-red-700 dark:text-neutral-400 dark:hover:text-red-400"
                    onClick={() =>
                      onChange({
                        ...pages,
                        [side.key]: pages[side.key].filter((each) => each.fileId !== page.fileId)
                      })
                    }
                  >
                    Remove
                  </button>
                </div>
                {!page.printable && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300">
                    <ExclamationTriangleIcon className="size-3.5 shrink-0" />
                    This page won't print. Upload a PDF.
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
};
